/**
 * Serviço de sincronização e integração da Gestão Comercial com o Escalador_backend.
 */
import { normalizeOptions, legacyOptionAliases } from './catalog.js'

const parseJson = (text, fallback) => {
  if (!text) return fallback
  try { return JSON.parse(text) ?? fallback } catch (e) { return fallback }
}
// Id do servidor ou null; um id "draft:" nunca é enviado (viraria NaN).
const backendId = value => value && !String(value).startsWith('draft:') && !String(value).startsWith('example-') ? Number(value) : null
// Cadastro sem contatos em JSON (anterior à aba de contatos): o contato principal vem dos campos da API.
// Sem isso o salvar derivava o e-mail da lista vazia e gravava e-mail em branco.
const contactsOrPrincipal = (contacts, { name, email, phone, mobile }) => Array.isArray(contacts) && contacts.length
  ? contacts
  : (email || phone || mobile ? [{ name: name || '', email: email || '', phone: phone || '', mobile: mobile || '', active: true, principal: true, fiscal: false, financial: false, userId: '' }] : [])

export function mapPlanToBackend(record) {
  return {
    name: record.name,
    description: record.description || record.name,
    maxCompanies: Number(record.included?.companies) || 1,          // backend exige ≥ 1
    maxUsersPerCompany: Number(record.included?.users) || 1,        // backend exige ≥ 1
    maxOnlinePdv: Number(record.included?.pdvTerminals ?? record.included?.onlinePdv ?? 0),
    maxOfflinePdv: Number(record.included?.offlinePdv ?? 0),
    monthlyPrice: record.basePrice != null ? Number(record.basePrice) : 0,
    additionalUserPrice: 0,
    additionalCompanyPrice: 0,
    allowAdditionalUsers: true,
    allowAdditionalCompanies: true,
    status: record.active !== false ? 'ACTIVE' : 'INACTIVE',
    billingCycleDays: 30,
    gracePeriodDays: 7,
    implementationFee: 0,
    commercialOptions: Array.isArray(record.modules) ? record.modules : [],
    representativeIdsJson: JSON.stringify((record.representativeIds || []).map(String)),
    commercialMode: record.mode || 'negotiated'
  }
}

export function mapPlanFromBackend(p) {
  return {
    id: String(p.id),
    name: p.name || 'Plano ' + p.id,
    description: p.description || '',
    mode: p.commercialMode || 'negotiated',
    basePrice: p.monthlyPrice != null ? Number(p.monthlyPrice) : 0,
    modules: normalizeOptions(Array.from(p.commercialOptions || [])),
    representativeIds: (parseJson(p.representativeIdsJson, []) || []).map(String),
    included: {
      users: p.maxUsersPerCompany ?? 1,
      companies: p.maxCompanies ?? 1,
      pdvTerminals: p.maxOnlinePdv ?? 0,
      offlinePdv: p.maxOfflinePdv ?? 0
    },
    active: p.status === 'ACTIVE'
  }
}

export function mapSubscriptionToBackend(record, db) {
  const companyId = record.companyIds && record.companyIds.length ? backendId(record.companyIds[0]) : null
  const planId = backendId(record.planId)
  const representativeId = backendId(record.representativeId)
  
  const plan = db?.plans?.find(p => String(p.id) === String(record.planId))
  const company = db?.companies?.find(c => String(c.id) === String(companyId))
  const manager = db?.managers?.find(m => String(m.id) === String(company?.managerId))
  const rep = db?.representatives?.find(r => String(r.id) === String(representativeId))
  const responsible = record.responsibleName || company?.contactName || manager?.name || company?.tradeName || company?.name || rep?.name || 'Responsável'

  const contractedOpts = new Set([
    ...(plan?.modules || []),
    ...(record.extraModules || []),
    ...(record.quote?.lockedModules || [])
  ])

  const totalAmount = record.quote?.totalCents ? (record.quote.totalCents / 100) : (record.baseAmount || 0)
  const subBaseAmount = record.quote?.baseCents != null ? (record.quote.baseCents / 100) : (record.baseAmount || (plan?.basePrice != null ? Number(plan.basePrice) : totalAmount))
  const subTotalAmount = record.quote?.totalCents != null ? (record.quote.totalCents / 100) : (totalAmount || subBaseAmount)
  const subAdditionalAmount = Math.max(0, subTotalAmount - subBaseAmount)
  const subMonthlyAmount = subTotalAmount

  let startIso = record.subscriptionStart ? String(record.subscriptionStart).replace('Z', '').slice(0, 19) : new Date().toISOString().slice(0, 19)
  if (!startIso.includes('T')) startIso += 'T00:00:00'

  let endIso = null
  if (record.subscriptionEnd) {
    endIso = String(record.subscriptionEnd).replace('Z', '').slice(0, 19)
    if (!endIso.includes('T')) endIso += 'T23:59:59'
  }

  let dueDay = record.billingDueDay != null ? Number(record.billingDueDay) : 10
  if (isNaN(dueDay) || dueDay < 1 || dueDay > 28) dueDay = 10

  let grace = record.gracePeriodDays != null ? Number(record.gracePeriodDays) : 7
  if (isNaN(grace) || grace < 0) grace = 7

  return {
    companyIds: companyId ? [companyId] : [],
    planId: planId,
    representativeId: representativeId,
    responsibleName: responsible,
    additionalUsersContracted: record.additional?.users || 0,
    additionalPdvContracted: Number(record.additional?.pdvTerminals) || 0,
    additionalCompaniesContracted: 0,
    pricingTableId: backendId(record.tableId || record.quote?.tableId),
    quoteJson: record.quote ? JSON.stringify(record.quote) : null,
    moduleContractOrderJson: JSON.stringify(record.moduleContractOrder || {}),
    subscriptionStart: startIso,
    subscriptionEnd: endIso,
    status: record.status || 'ACTIVE',
    baseAmount: subBaseAmount,
    additionalAmount: subAdditionalAmount,
    monthlyAmount: subMonthlyAmount,
    billingCycleDays: 30,
    gracePeriodDays: grace,
    billingDueDay: dueDay,
    billingEmail: record.billingEmail || null,
    contractedOptions: Array.from(contractedOpts)
  }
}

export function mapSubscriptionFromBackend(s, db) {
  const companyId = s.companyIds && s.companyIds.length ? String(s.companyIds[0]) : ''
  const company = db?.companies?.find(c => String(c.id) === companyId)
  const companyName = company?.tradeName || company?.name || (s.companyIds?.length ? 'Empresa #' + s.companyIds[0] : 'Licença')

  const plan = db?.plans?.find(p => String(p.id) === String(s.planId))
  const planName = s.planName || (plan ? plan.name : ('Plano #' + (s.planId || '')))

  const savedQuote = parseJson(s.quoteJson, null)
  let repId = s.representativeId ? String(s.representativeId) : ''   // agora vem também na lista
  if (!repId && company?.representativeId) {
    repId = String(company.representativeId)
  }
  if (!repId && s.representativeName && db?.representatives) {
    const rep = db.representatives.find(r => r.name === s.representativeName || r.tradeName === s.representativeName)
    if (rep) repId = String(rep.id)
  }

  return {
    id: String(s.id),
    name: 'Licença · ' + companyName,
    companyIds: (s.companyIds || []).map(String),
    planId: s.planId ? String(s.planId) : '',
    representativeId: repId,
    tableId: s.pricingTableId ? String(s.pricingTableId) : (savedQuote?.tableId ? String(savedQuote.tableId) : ''),
    requestedUsers: savedQuote?.users?.requested ?? s.currentMaxUsers ?? 1,
    extraModules: normalizeOptions(Array.from(s.contractedOptions || [])),
    moduleContractOrder: parseJson(s.moduleContractOrderJson, {}),
    billingDueDay: s.billingDueDay || 10,
    gracePeriodDays: s.gracePeriodDays != null ? s.gracePeriodDays : 7,
    billingEmail: s.billingEmail || '',
    subscriptionStart: s.subscriptionStart || null,
    subscriptionEnd: s.subscriptionEnd || null,
    status: s.status || 'ACTIVE',
    active: s.status === 'ACTIVE' || s.status === 'GRACE_PERIOD',
    additional: { users: s.additionalUsersContracted || 0, pdvTerminals: s.additionalPdvContracted || 0 },
    quote: savedQuote || {
      planName, lines: [], lockedModules: [],
      included: { users: s.currentMaxUsers || 1, pdvTerminals: 0 },
      totalCents: Math.round(Number(s.monthlyAmount || 0) * 100)
    }
  }
}

export function mapCompanyToBackend(record) {
  const cleanCpfCnpj = record.document ? String(record.document).replace(/\D/g, '') : null
  const cleanPhone = record.phone ? String(record.phone).replace(/\D/g, '') : null
  const cleanMobile = record.mobile ? String(record.mobile).replace(/\D/g, '') : null
  const representativeId = backendId(record.representativeId)
  const managerId = backendId(record.managerId)
  const groupId = backendId(record.groupId)
  const accountingOfficeId = backendId(record.accountingOfficeId)

  return {
    basicData: {
      businessName: record.name || record.businessName || '',
      tradeName: record.tradeName || record.name || '',
      cnpj: cleanCpfCnpj,
      stateRegistration: record.stateRegistration || null,
      municipalRegistration: record.municipalRegistration || null
    },
    contact: {
      nameContact: record.contactName || record.name || '',
      mainPhone: cleanPhone,
      secondaryPhone: cleanMobile,
      email: record.email || null, // vazio = null: o servidor tratava '' como e-mail repetido e gravava '' por cima
      site: record.site || null
    },
    address: {
      address: record.street || '',
      number: record.number || '',
      complement: record.complement || '',
      neighborhood: record.district || '',
      city: record.city || '',
      state: record.state || '',
      zipCode: record.postalCode ? String(record.postalCode).replace(/\D/g, '') : ''
    },
    legalResponsible: {
      responsibleName: record.legalName || null,
      responsibleCPF: record.legalCpf ? String(record.legalCpf).replace(/\D/g, '') : null
    },
    // regime tributário e CNAE não eram enviados (a tela dizia "salvo" e nada gravava); o backend só
    // altera os campos fiscais não nulos, então certificado, CSC e numeração ficam como estão
    taxData: {
      taxRegime: record.taxRegime || null,
      mainCNAE: record.activity || null,
      // código IBGE do endereço principal: a NF-e usa taxData.codMunicipio (antes ficava só no JSON de endereços)
      codMunicipio: ((record.addresses || []).find(a => a.type === 'principal') || (record.addresses || [])[0] || {}).municipalityCode || record.municipalityCode || null
    },
    status: record.active !== false ? 'ACTIVE' : 'INACTIVE',
    segment: 'RETAIL',
    representativeId: representativeId,
    managerId: managerId,
    groupId: groupId,
    accountingOfficeId: accountingOfficeId,
    contactsJson: Array.isArray(record.contacts) ? JSON.stringify(record.contacts) : null,
    addressesJson: Array.isArray(record.addresses) ? JSON.stringify(record.addresses) : null,
    notes: record.notes || null
  }
}

export function mapCompanyFromBackend(c) {
  let contacts = []
  let addresses = []
  try {
    if (c.contactsJson) contacts = JSON.parse(c.contactsJson)
  } catch (e) { /* ignore */ }
  try {
    if (c.addressesJson) addresses = JSON.parse(c.addressesJson)
  } catch (e) { /* ignore */ }

  if (!addresses.length && c.address) {
    addresses = [{
      type: 'principal',
      postalCode: c.address.zipCode || '',
      street: c.address.address || '',
      number: c.address.number || '',
      complement: c.address.complement || '',
      district: c.address.neighborhood || '',
      city: c.address.city || '',
      state: c.address.state || ''
    }]
  }

  let formattedDoc = c.basicData?.cnpj || ''
  if (formattedDoc.length === 14) {
    formattedDoc = formattedDoc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  } else if (formattedDoc.length === 11) {
    formattedDoc = formattedDoc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }

  // "name" é a Razão social do formulário (PersonRegistration): antes vinha do nome fantasia e o salvar
  // gravava o fantasia por cima da razão social (businessName)
  const name = c.basicData?.businessName || c.basicData?.tradeName || c.companyCode || ('Empresa #' + c.id)
  return {
    id: String(c.id),
    name: name,
    taxRegime: c.taxRegime || c.taxData?.taxRegime || '',
    activity: c.mainCNAE || c.taxData?.mainCNAE || '',
    municipalityCode: c.codMunicipio || c.taxData?.codMunicipio || '',
    tradeName: c.basicData?.tradeName || '',
    businessName: c.basicData?.businessName || '',
    document: formattedDoc,
    personType: formattedDoc.length === 11 ? 'individual' : 'business',
    stateRegistration: c.basicData?.stateRegistration || '',
    municipalRegistration: c.basicData?.municipalRegistration || '',
    email: c.contact?.email || '',
    phone: c.contact?.mainPhone || '',
    mobile: c.contact?.secondaryPhone || '',
    site: c.contact?.site || '',
    contactName: c.contact?.nameContact || '',
    active: c.status === 'ACTIVE',
    representativeId: c.representativeId ? String(c.representativeId) : '',
    managerId: c.managerId ? String(c.managerId) : '',
    groupId: c.groupId ? String(c.groupId) : '',
    accountingOfficeId: c.accountingOfficeId ? String(c.accountingOfficeId) : '',
    notes: c.notes || '',
    contacts: contactsOrPrincipal(contacts, { name: c.contact?.nameContact, email: c.contact?.email, phone: c.contact?.mainPhone, mobile: c.contact?.secondaryPhone }),
    addresses: Array.isArray(addresses) ? addresses : []
  }
}

export function mapRepresentativeToBackend(record, extraPayload) {
  const cleanCpfCnpj = record.document ? String(record.document).replace(/\D/g, '') : null
  const cleanPhone = record.phone ? String(record.phone).replace(/\D/g, '') : null
  const cleanMobile = record.mobile ? String(record.mobile).replace(/\D/g, '') : null

  return {
    basicData: {
      businessName: record.name || record.businessName || '',
      tradeName: record.tradeName || record.name || '',
      cnpj: cleanCpfCnpj,
      stateRegistration: record.stateRegistration || null,
      municipalRegistration: record.municipalRegistration || null
    },
    contact: {
      nameContact: record.contactName || record.name || '',
      mainPhone: cleanPhone,
      secondaryPhone: cleanMobile,
      email: record.email || null, // vazio = null: o servidor tratava '' como e-mail repetido e gravava '' por cima
      site: record.site || null
    },
    address: {
      address: record.street || '',
      number: record.number || '',
      complement: record.complement || '',
      neighborhood: record.district || '',
      city: record.city || '',
      state: record.state || '',
      zipCode: record.postalCode ? String(record.postalCode).replace(/\D/g, '') : ''
    },
    password: extraPayload?.password || record.password || null,
    commissionType: record.commissionType || null,
    commissionRate: record.commissionRate != null ? Number(record.commissionRate) : null,
    status: record.active !== false ? 'ACTIVE' : 'INACTIVE',
    defaultPlanId: backendId(record.defaultPlanId),
    defaultTableId: backendId(record.defaultTableId),
    priceTableIdsJson: JSON.stringify((Array.isArray(record.tableIds) ? record.tableIds : []).map(String).filter(id => !id.startsWith('draft:'))),
    contactsJson: Array.isArray(record.contacts) ? JSON.stringify(record.contacts) : null,
    addressesJson: Array.isArray(record.addresses) ? JSON.stringify(record.addresses) : null,
    notes: record.notes || null
  }
}

export function mapRepresentativeFromBackend(r) {
  let contacts = []
  let addresses = []
  try {
    if (r.contactsJson) contacts = JSON.parse(r.contactsJson)
  } catch (e) { /* ignore */ }
  try {
    if (r.addressesJson) addresses = JSON.parse(r.addressesJson)
  } catch (e) { /* ignore */ }

  if (!addresses.length && r.address) {
    addresses = [{
      type: 'principal',
      postalCode: r.address.zipCode || '',
      street: r.address.address || '',
      number: r.address.number || '',
      complement: r.address.complement || '',
      district: r.address.neighborhood || '',
      city: r.address.city || '',
      state: r.address.state || ''
    }]
  }

  let formattedDoc = r.basicData?.cnpj || ''
  if (formattedDoc.length === 14) {
    formattedDoc = formattedDoc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  } else if (formattedDoc.length === 11) {
    formattedDoc = formattedDoc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }

  const name = r.basicData?.businessName || r.basicData?.tradeName || ('Representante #' + r.id)
  return {
    id: String(r.id),
    name: name,
    tradeName: r.basicData?.tradeName || '',
    document: formattedDoc,
    personType: formattedDoc.length === 11 ? 'individual' : 'business',
    stateRegistration: r.basicData?.stateRegistration || '',
    municipalRegistration: r.basicData?.municipalRegistration || '',
    email: r.contact?.email || '',
    phone: r.contact?.mainPhone || '',
    mobile: r.contact?.secondaryPhone || '',
    site: r.contact?.site || '',
    contactName: r.contact?.nameContact || '',
    active: r.status === 'ACTIVE',
    defaultPlanId: r.defaultPlanId ? String(r.defaultPlanId) : '',
    defaultTableId: r.defaultTableId ? String(r.defaultTableId) : '',
    tableIds: (parseJson(r.priceTableIdsJson, null) || (r.defaultTableId ? [String(r.defaultTableId)] : [])).map(String),
    notes: r.notes || '',
    contacts: contactsOrPrincipal(contacts, { name: r.contact?.nameContact, email: r.contact?.email, phone: r.contact?.mainPhone, mobile: r.contact?.secondaryPhone }),
    addresses: Array.isArray(addresses) ? addresses : []
  }
}

export function mapManagerToBackend(record, extraPayload) {
  const representativeId = backendId(record.representativeId)
  const password = extraPayload?.password || record.password || null
  const cleanCpf = record.cpf ? String(record.cpf).replace(/\D/g, '') : null

  return {
    representativeId,
    name: record.name,
    email: record.email ? String(record.email).trim().toLowerCase() : '',
    cpf: cleanCpf,
    password: password || null,
    active: record.active !== false,
    requirePasswordChange: record.requirePasswordChange !== false
  }
}

export function mapManagerFromBackend(m) {
  let formattedCpf = m.cpf || ''
  if (formattedCpf.length === 11) {
    formattedCpf = formattedCpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }

  return {
    id: String(m.id),
    name: m.name || '',
    email: m.email || '',
    cpf: formattedCpf,
    representativeId: m.representativeId ? String(m.representativeId) : '',
    active: m.active !== false,
    requirePasswordChange: m.requirePasswordChange !== false,
    createdAt: m.createdAt
  }
}

export function mapBusinessGroupToBackend(record) {
  const representativeId = backendId(record.representativeId)
  return {
    name: record.name,
    representativeId
  }
}

export function mapBusinessGroupFromBackend(g) {
  return {
    id: String(g.id),
    name: g.name || '',
    representativeId: g.representativeId ? String(g.representativeId) : '',
    createdAt: g.createdAt
  }
}

export function mapTableToBackend(record) {
  const rules = record.userRules || { initialUsers: 1, blockSize: 1, maxAdditionalUsers: null, moduleGrowthEnabled: false }
  return {
    name: record.name,
    initialUsers: rules.initialUsers != null ? Number(rules.initialUsers) : 1,
    blockSize: rules.blockSize != null ? Number(rules.blockSize) : 1,
    maxAdditionalUsers: rules.maxAdditionalUsers != null ? Number(rules.maxAdditionalUsers) : null,
    moduleGrowthEnabled: !!rules.moduleGrowthEnabled,
    extraPriceUsers: record.extraPrices?.users != null ? Number(record.extraPrices.users) : null,
    extraPricePdv: record.extraPrices?.pdvTerminals != null ? Number(record.extraPrices.pdvTerminals) : null,
    modulePricesJson: record.modulePrices ? JSON.stringify(record.modulePrices) : null,
    moduleIncrementsJson: record.moduleIncrements ? JSON.stringify(record.moduleIncrements) : null,
    groupDiscountsJson: record.groupDiscounts ? JSON.stringify(record.groupDiscounts) : null,
    representativeSalesJson: JSON.stringify(record.representativeSales || {}),
    groupDiscountPercent: record.groupDiscountPercent != null && record.groupDiscountPercent !== '' ? Number(record.groupDiscountPercent) : null,
    status: record.active !== false ? 'ACTIVE' : 'INACTIVE'
  }
}

export function mapTableFromBackend(t) {
  const modulePrices = parseJson(t.modulePricesJson, {})
  const moduleIncrements = parseJson(t.moduleIncrementsJson, {})
  const groupDiscounts = parseJson(t.groupDiscountsJson, [])

  const legacyModulePrices = {}
  const legacyModuleIncrements = {}
  for (const [field, legacyTarget] of [[modulePrices, legacyModulePrices], [moduleIncrements, legacyModuleIncrements]]) {
    for (const id of Object.keys(field || {})) {
      if (!Object.prototype.hasOwnProperty.call(legacyOptionAliases, id)) continue
      legacyTarget[id] = field[id]
      const target = legacyOptionAliases[id]
      if (target && !Object.prototype.hasOwnProperty.call(field, target)) field[target] = null
      delete field[id]
    }
  }

  return {
    id: String(t.id),
    name: t.name || 'Tabela ' + t.id,
    modulePrices: modulePrices || {},
    moduleIncrements: moduleIncrements || {},
    ...(Object.keys(legacyModulePrices).length ? { legacyModulePrices } : {}),
    ...(Object.keys(legacyModuleIncrements).length ? { legacyModuleIncrements } : {}),
    extraPrices: {
      users: t.extraPriceUsers != null ? Number(t.extraPriceUsers) : null,
      pdvTerminals: t.extraPricePdv != null ? Number(t.extraPricePdv) : null
    },
    userRules: {
      initialUsers: t.initialUsers != null ? Number(t.initialUsers) : 1,
      blockSize: t.blockSize != null ? Number(t.blockSize) : 1,
      maxAdditionalUsers: t.maxAdditionalUsers != null ? Number(t.maxAdditionalUsers) : null,
      moduleGrowthEnabled: !!t.moduleGrowthEnabled
    },
    groupDiscounts: Array.isArray(groupDiscounts) ? groupDiscounts : [],
    representativeSales: parseJson(t.representativeSalesJson, {}),
    ...(t.groupDiscountPercent != null ? { groupDiscountPercent: Number(t.groupDiscountPercent) } : {}),
    active: t.status === 'ACTIVE'
  }
}

export function mapPanelUserToBackend(record, extraPayload) {
  const representativeId = record.representativeId ? Number(record.representativeId) : null
  const password = extraPayload?.password || record.password || null
  const cleanCpf = record.cpf ? String(record.cpf).replace(/\D/g, '') : null

  return {
    representativeId,
    name: record.name,
    email: record.email ? String(record.email).trim().toLowerCase() : '',
    cpf: cleanCpf,
    password: password || null,
    active: record.active !== false,
    requirePasswordChange: record.requirePasswordChange !== false,
    permissions: Array.isArray(record.permissions) ? record.permissions : []
  }
}

export function mapPanelUserFromBackend(u) {
  let formattedCpf = u.cpf || ''
  if (formattedCpf.length === 11) {
    formattedCpf = formattedCpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }

  return {
    id: String(u.id),
    name: u.name || '',
    email: u.email || '',
    cpf: formattedCpf,
    representativeId: u.representativeId ? String(u.representativeId) : '',
    active: u.active !== false,
    requirePasswordChange: u.requirePasswordChange !== false,
    permissions: Array.isArray(u.permissions) ? [...u.permissions] : [],
    createdAt: u.createdAt
  }
}

export function mapAccountingOfficeToBackend(record) {
  const representativeId = record.representativeId ? Number(record.representativeId) : null
  const cleanCpfCnpj = record.document ? String(record.document).replace(/\D/g, '') : null

  return {
    representativeId,
    name: record.name,
    tradeName: record.tradeName || null,
    document: cleanCpfCnpj || record.document || null,
    personType: record.personType || 'business',
    stateRegistration: record.stateRegistration || null,
    municipalRegistration: record.municipalRegistration || null,
    birthOpeningDate: record.birthOpeningDate || null,
    taxRegime: record.taxRegime || null,
    activity: record.activity || null,
    legalName: record.legalName || null,
    legalCpf: record.legalCpf ? String(record.legalCpf).replace(/\D/g, '') : null,
    notes: record.notes || null,
    active: record.active !== false,
    postalCode: record.postalCode || null,
    street: record.street || null,
    number: record.number || null,
    complement: record.complement || null,
    district: record.district || null,
    city: record.city || null,
    state: record.state || null,
    email: record.email || null,
    phone: record.phone || null,
    mobile: record.mobile || null,
    contactName: record.contactName || null,
    contactsJson: Array.isArray(record.contacts) ? JSON.stringify(record.contacts) : null,
    addressesJson: Array.isArray(record.addresses) ? JSON.stringify(record.addresses) : null
  }
}

export function mapAccountingOfficeFromBackend(o) {
  let contacts = []
  let addresses = []
  try {
    if (o.contactsJson) contacts = JSON.parse(o.contactsJson)
  } catch (e) { /* ignore */ }
  try {
    if (o.addressesJson) addresses = JSON.parse(o.addressesJson)
  } catch (e) { /* ignore */ }

  if (!addresses.length && (o.city || o.state || o.street)) {
    addresses = [{
      type: 'principal',
      postalCode: o.postalCode || '',
      street: o.street || '',
      number: o.number || '',
      complement: o.complement || '',
      district: o.district || '',
      city: o.city || '',
      state: o.state || ''
    }]
  }

  let formattedDoc = o.document || ''
  if (formattedDoc.length === 14) {
    formattedDoc = formattedDoc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  } else if (formattedDoc.length === 11) {
    formattedDoc = formattedDoc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }

  return {
    id: String(o.id),
    name: o.name || '',
    tradeName: o.tradeName || '',
    document: formattedDoc,
    personType: o.personType || 'business',
    stateRegistration: o.stateRegistration || '',
    municipalRegistration: o.municipalRegistration || '',
    birthOpeningDate: o.birthOpeningDate || '',
    taxRegime: o.taxRegime || '',
    activity: o.activity || '',
    legalName: o.legalName || '',
    legalCpf: o.legalCpf || '',
    representativeId: o.representativeId ? String(o.representativeId) : '',
    active: o.active !== false,
    notes: o.notes || '',
    postalCode: o.postalCode || '',
    street: o.street || '',
    number: o.number || '',
    complement: o.complement || '',
    district: o.district || '',
    city: o.city || '',
    state: o.state || '',
    email: o.email || '',
    phone: o.phone || '',
    mobile: o.mobile || '',
    contactName: o.contactName || '',
    contacts: contactsOrPrincipal(contacts, { name: o.contactName, email: o.email, phone: o.phone, mobile: o.mobile }),
    addresses: Array.isArray(addresses) ? addresses : []
  }
}

export function mapAccountingUserToBackend(record, extraPayload) {
  const officeId = record.officeId ? Number(record.officeId) : null
  const representativeId = record.representativeId ? Number(record.representativeId) : null
  const password = extraPayload?.password || record.password || null
  const cleanCpf = record.cpf ? String(record.cpf).replace(/\D/g, '') : null

  return {
    officeId,
    representativeId,
    name: record.name,
    email: record.email ? String(record.email).trim().toLowerCase() : '',
    cpf: cleanCpf,
    password: password || null,
    active: record.active !== false,
    canExport: record.canExport !== false,
    canAccessERP: !!record.canAccessERP,
    requirePasswordChange: record.requirePasswordChange !== false
  }
}

export function mapAccountingUserFromBackend(u) {
  let formattedCpf = u.cpf || ''
  if (formattedCpf.length === 11) {
    formattedCpf = formattedCpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }

  return {
    id: String(u.id),
    name: u.name || '',
    email: u.email || '',
    cpf: formattedCpf,
    officeId: u.officeId ? String(u.officeId) : '',
    representativeId: u.representativeId ? String(u.representativeId) : '',
    active: u.active !== false,
    canExport: u.canExport !== false,
    canAccessERP: !!u.canAccessERP,
    requirePasswordChange: u.requirePasswordChange !== false,
    createdAt: u.createdAt
  }
}

/**
 * Salva um registro diretamente no Escalador_backend
 */
export async function saveRecordToBackend(axios, urlRest, kind, record, db, extraPayload) {
  const cleanUrl = urlRest.replace(/\/+$/, '')

  if (kind === 'tables') {
    const payload = mapTableToBackend(record)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')

    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/pricing-table`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/pricing-table/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  if (kind === 'plans') {
    const payload = mapPlanToBackend(record)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')
    
    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/plan`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/plan/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  if (kind === 'subscriptions') {
    const payload = mapSubscriptionToBackend(record, db, extraPayload?.user)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')

    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/subscription`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/subscription/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  if (kind === 'panelUsers') {
    const payload = mapPanelUserToBackend(record, extraPayload)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')

    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/representative-user`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/representative-user/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  if (kind === 'accountingOffices') {
    const payload = mapAccountingOfficeToBackend(record)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')
    let resultData
    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/accounting-office`, payload)
      resultData = data
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/accounting-office/${record.id}`, payload)
      resultData = data
    }
    const realOfficeId = String(resultData?.id || record.id)

    if (Array.isArray(extraPayload?.team)) {
      for (const member of extraPayload.team) {
        try {
          const userPayload = mapAccountingUserToBackend({ ...member, officeId: realOfficeId, representativeId: record.representativeId }, member)
          const isUserNew = !member.id || String(member.id).startsWith('draft:') || String(member.id).startsWith('example-')
          if (isUserNew) {
            const { data } = await axios.post(`${cleanUrl}/gerencial/accounting-user`, userPayload)
            if (data?.id) {
              const oldId = String(member.id)
              const newId = String(data.id)
              member.id = newId
              if (db?.accountingUsers) {
                const target = db.accountingUsers.find(u => String(u.id) === oldId)
                if (target) target.id = newId
              }
            }
          } else {
            await axios.put(`${cleanUrl}/gerencial/accounting-user/${member.id}`, userPayload)
          }
        } catch (uErr) {
          console.warn('Erro ao sincronizar usuário contábil da equipe:', uErr)
          const errorMsg = uErr.response?.data?.message || uErr.message || 'Falha ao sincronizar usuário contábil'
          throw new Error(`Falha ao salvar usuário contábil (${member.name || member.email}): ${errorMsg}`)
        }
      }
    }

    return { id: realOfficeId, data: resultData }
  }

  if (kind === 'accountingUsers') {
    const payload = mapAccountingUserToBackend(record, extraPayload)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')

    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/accounting-user`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/accounting-user/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  if (kind === 'representatives') {
    const payload = mapRepresentativeToBackend(record, extraPayload)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')
    let resultData
    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/representative`, payload)
      resultData = data
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/representative/${record.id}`, payload)
      resultData = data
    }
    const realRepId = String(resultData?.id || record.id)

    if (Array.isArray(extraPayload?.team)) {
      for (const member of extraPayload.team) {
        try {
          const userPayload = mapPanelUserToBackend({ ...member, representativeId: realRepId }, member)
          const isUserNew = !member.id || String(member.id).startsWith('draft:') || String(member.id).startsWith('example-')
          if (isUserNew) {
            const { data } = await axios.post(`${cleanUrl}/gerencial/representative-user`, userPayload)
            if (data?.id) {
              const oldId = String(member.id)
              const newId = String(data.id)
              member.id = newId
              if (db?.panelUsers) {
                const target = db.panelUsers.find(u => String(u.id) === oldId)
                if (target) target.id = newId
              }
            }
          } else {
            await axios.put(`${cleanUrl}/gerencial/representative-user/${member.id}`, userPayload)
          }
        } catch (uErr) {
          console.warn('Erro ao sincronizar usuário do painel da equipe:', uErr)
          const errorMsg = uErr.response?.data?.message || uErr.message || 'Falha ao sincronizar usuário da equipe'
          throw new Error(`Falha ao salvar usuário da equipe (${member.name || member.email}): ${errorMsg}`)
        }
      }
    }

    if (Array.isArray(extraPayload?.planIds)) {
      for (const plan of db?.plans || []) {
        const before = extraPayload.planIdsBefore || []
        const changed = extraPayload.planIds.includes(plan.id) !== before.includes(plan.id)
        if (!changed || String(plan.id).startsWith('draft:')) continue
        // plan.representativeIds já foi atualizado pelo saveCommercialBundle; só troca o id em rascunho pelo real
        const representativeIds = plan.representativeIds.map(id => String(id) === String(record.id) ? realRepId : String(id))
        plan.representativeIds = representativeIds
        await axios.put(`${cleanUrl}/gerencial/plan/${plan.id}`, mapPlanToBackend(plan))
      }
    }

    return { id: realRepId, data: resultData }
  }

  if (kind === 'companies') {
    if (extraPayload?.manager && (!record.managerId || String(record.managerId).startsWith('draft:'))) {
      const oldManagerId = record.managerId
      const managerPayload = mapManagerToBackend({ ...extraPayload.manager, representativeId: record.representativeId, requirePasswordChange: true }, extraPayload.manager)
      const { data: manager } = await axios.post(`${cleanUrl}/gerencial/manager`, managerPayload)
      record.managerId = String(manager.id)
      const local = db?.managers?.find(m => String(m.id) === String(oldManagerId))
      if (local) local.id = record.managerId
    }

    const payload = mapCompanyToBackend(record)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')

    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/company`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/company/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  if (kind === 'managers') {
    const payload = mapManagerToBackend(record, extraPayload)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')

    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/manager`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/manager/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  if (kind === 'businessGroups') {
    const payload = mapBusinessGroupToBackend(record)
    const isNew = !record.id || String(record.id).startsWith('draft:') || String(record.id).startsWith('example-')

    if (isNew) {
      const { data } = await axios.post(`${cleanUrl}/gerencial/business-group`, payload)
      return { id: String(data.id || record.id), data }
    } else {
      const { data } = await axios.put(`${cleanUrl}/gerencial/business-group/${record.id}`, payload)
      return { id: String(record.id), data }
    }
  }

  // Para outros tipos, se não houver endpoint dedicado, retorna sucesso sem erro
  return { id: String(record.id) }
}

/**
 * Carrega registros reais do backend para preencher o db
 */
export async function loadBackendData(axios, urlRest) {
  const cleanUrl = urlRest.replace(/\/+$/, '')
  const failed = []
  const results = {
    tables: [],
    plans: [],
    subscriptions: [],
    companies: [],
    representatives: [],
    panelUsers: [],
    accountingOffices: [],
    accountingUsers: [],
    businessGroups: [],
    managers: []
  }

  const calls = [
    axios.get(`${cleanUrl}/gerencial/pricing-table`).then(res => {
      if (Array.isArray(res.data)) results.tables = res.data.map(mapTableFromBackend)
    }).catch(err => { failed.push('tabelas de preços'); console.warn('Erro ao carregar tabelas de preços:', err) }),

    axios.get(`${cleanUrl}/gerencial/plan`).then(res => {
      if (Array.isArray(res.data)) results.plans = res.data.map(mapPlanFromBackend)
    }).catch(err => { failed.push('planos'); console.warn('Erro ao carregar planos:', err) }),

    axios.get(`${cleanUrl}/gerencial/company?filter=ALL&page=0&size=1000`).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.content || [])
      results.companies = list.map(mapCompanyFromBackend)
    }).catch(err => { failed.push('empresas'); console.warn('Erro ao carregar empresas:', err) }),

    axios.get(`${cleanUrl}/gerencial/representative?filter=ALL&page=0&size=1000`).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.content || [])
      results.representatives = list.map(mapRepresentativeFromBackend)
    }).catch(err => { failed.push('representantes'); console.warn('Erro ao carregar representantes:', err) }),

    axios.get(`${cleanUrl}/gerencial/representative-user`).then(res => {
      if (Array.isArray(res.data)) results.panelUsers = res.data.map(mapPanelUserFromBackend)
    }).catch(err => { failed.push('usuários de representantes'); console.warn('Erro ao carregar usuários de representantes:', err) }),

    axios.get(`${cleanUrl}/gerencial/accounting-office`).then(res => {
      if (Array.isArray(res.data)) results.accountingOffices = res.data.map(mapAccountingOfficeFromBackend)
    }).catch(err => { failed.push('contabilidades'); console.warn('Erro ao carregar contabilidades:', err) }),

    axios.get(`${cleanUrl}/gerencial/accounting-user`).then(res => {
      if (Array.isArray(res.data)) results.accountingUsers = res.data.map(mapAccountingUserFromBackend)
    }).catch(err => { failed.push('usuários contábeis'); console.warn('Erro ao carregar usuários contábeis:', err) }),

    axios.get(`${cleanUrl}/gerencial/business-group`).then(res => {
      if (Array.isArray(res.data)) results.businessGroups = res.data.map(mapBusinessGroupFromBackend)
    }).catch(err => { failed.push('grupos empresariais'); console.warn('Erro ao carregar grupos empresariais:', err) }),

    axios.get(`${cleanUrl}/gerencial/manager`).then(res => {
      if (Array.isArray(res.data)) results.managers = res.data.map(mapManagerFromBackend)
    }).catch(err => { failed.push('gestores'); console.warn('Erro ao carregar gestores:', err) })
  ]

  await Promise.allSettled(calls)

  // Carrega assinaturas depois para vincular nomes de empresas
  try {
    const subRes = await axios.get(`${cleanUrl}/gerencial/subscription?filter=ALL&page=0&size=1000`)
    const list = Array.isArray(subRes.data) ? subRes.data : (subRes.data?.content || [])
    results.subscriptions = list.map(s => mapSubscriptionFromBackend(s, results))
  } catch (err) {
    failed.push('assinaturas')
    console.warn('Erro ao carregar assinaturas:', err)
  }

  return { ...results, failed }
}

export function replaceDraftId(database, kind, oldId, newId) {
  if (!oldId || String(oldId) === String(newId)) return
  const target = database[kind].find(item => String(item.id) === String(oldId))
  if (target) target.id = String(newId)
  // vínculos que apontam para o id antigo
  const links = { managers: ['companies', 'managerId'], businessGroups: ['companies', 'groupId'],
    accountingOffices: ['companies', 'accountingOfficeId'], tables: ['subscriptions', 'tableId'],
    plans: ['subscriptions', 'planId'], companies: null, representatives: null }
  const link = links[kind]
  if (link) for (const item of database[link[0]]) if (String(item[link[1]]) === String(oldId)) item[link[1]] = String(newId)
  if (kind === 'representatives') {
    for (const k of ['companies', 'subscriptions', 'panelUsers', 'businessGroups', 'accountingOffices', 'managers'])
      for (const item of database[k]) if (String(item.representativeId) === String(oldId)) item.representativeId = String(newId)
    for (const plan of database.plans) plan.representativeIds = plan.representativeIds.map(id => String(id) === String(oldId) ? String(newId) : id)
  }
  if (kind === 'tables') for (const rep of database.representatives) rep.tableIds = (rep.tableIds || []).map(id => String(id) === String(oldId) ? String(newId) : id)
  if (kind === 'companies') for (const sub of database.subscriptions) sub.companyIds = sub.companyIds.map(id => String(id) === String(oldId) ? String(newId) : id)
}

export async function persistRelatedQuotes(axios, urlRest, before, after, savedId) {
  const cleanUrl = urlRest.replace(/\/+$/, '')
  for (const sub of after.subscriptions || []) {
    if (String(sub.id) === String(savedId) || String(sub.id).startsWith('draft:')) continue
    const old = (before.subscriptions || []).find(item => String(item.id) === String(sub.id))
    if (!old) continue
    const quoteChanged = JSON.stringify(old.quote) !== JSON.stringify(sub.quote)
    const orderChanged = JSON.stringify(old.moduleContractOrder || {}) !== JSON.stringify(sub.moduleContractOrder || {})
    if (!quoteChanged && !orderChanged) continue
    const totalCents = sub.quote?.totalCents, baseCents = sub.quote?.baseCents
    await axios.put(`${cleanUrl}/gerencial/subscription/${sub.id}/quote`, {
      quoteJson: sub.quote ? JSON.stringify(sub.quote) : null,
      moduleContractOrderJson: JSON.stringify(sub.moduleContractOrder || {}),
      baseAmount: baseCents != null ? baseCents / 100 : null,
      monthlyAmount: totalCents != null ? totalCents / 100 : null,
      additionalAmount: totalCents != null && baseCents != null ? Math.max(0, totalCents - baseCents) / 100 : null
    })
  }
}


